/*
 * PDP V3 (TSK-11309 split test): product page behaviour and the gift steps
 * (card message, engraved plaque, garden extras for hampers, delivery date).
 * Markup: sections/main-product-v3.liquid and snippets/pdp3-steps.liquid.
 *
 * The helpers at the top never touch the DOM and are unit-tested with `node --test tests/pdp3.test.js`.
 */
(function () {
  'use strict';

  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function groupThousands(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** Format pence with a Shopify money format such as "£{{amount}}". `trim` drops a ".00". */
  function formatMoney(cents, format, trim) {
    const amount = (Number(cents) || 0) / 100;
    return (format || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key.indexOf('no_decimals') !== -1) return groupThousands(Math.round(amount).toString());
      const parts = amount.toFixed(2).split('.');
      return groupThousands(parts[0]) + (trim && parts[1] === '00' ? '' : '.' + parts[1]);
    });
  }

  /** How far `timeZone`'s wall clock is ahead of UTC at `epochMs`, in ms (BST: 3600000). */
  function zoneOffsetMs(epochMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date(epochMs));
    const get = type => Number(parts.find(p => p.type === type).value);
    const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return wall - Math.floor(epochMs / 1000) * 1000;
  }

  /** "2026-10-06 12:00:00" on the store's wall clock → epoch ms. NaN when unreadable. */
  function storeTimeToEpoch(text, timeZone, fallbackOffsetMinutes) {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(String(text || '').trim());
    if (!m) return NaN;
    const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    try {
      const firstGuess = wall - zoneOffsetMs(wall, timeZone);
      return wall - zoneOffsetMs(firstGuess, timeZone); // second pass settles clock-change days
    } catch (e) {
      return wall - (Number(fallbackOffsetMinutes) || 0) * 60000;
    }
  }

  /** Under a day "3h 08m", otherwise "1d 4h". Null once the time has passed. */
  function formatCountdown(ms) {
    if (!(ms > 0)) return null;
    const totalMinutes = Math.ceil(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    if (hours >= 24) return Math.floor(hours / 24) + 'd ' + (hours % 24) + 'h';
    return hours + 'h ' + String(totalMinutes % 60).padStart(2, '0') + 'm';
  }

  /** "2026-10-07" → "Wednesday" ("Wed" when short). */
  function weekdayOf(isoDate, short) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ''));
    if (!m) return '';
    const name = WEEKDAYS[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
    return short ? name.slice(0, 3) : name;
  }

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  /** "2026-10-09" → "Friday 9th October" ("Fri 9th Oct" when short). */
  function longDateOf(isoDate, short) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ''));
    if (!m) return '';
    const day = +m[3];
    const teen = day >= 11 && day <= 13;
    const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' })[day % 10] || 'th';
    const month = MONTHS[+m[2] - 1];
    return weekdayOf(isoDate, short) + ' ' + day + suffix + ' ' + (short ? month.slice(0, 3) : month);
  }

  /** The variant whose options equal `options` exactly, or null. */
  function findVariant(variants, options) {
    return variants.find(v => v.options.every((value, i) => value === options[i])) || null;
  }

  /** 1-based cart line for a line item key, or null when the line has gone. */
  function lineForKey(cart, key) {
    const items = cart && cart.items ? cart.items : [];
    const index = items.findIndex(item => item.key === key);
    return index === -1 ? null : index + 1;
  }

  /** ["a","b","c"] → "a, b and c". */
  function joinList(items) {
    if (items.length <= 1) return items.join('');
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /** Shopify-style handle: "Rectangle Plaque" → "rectangle-plaque". */
  function handleize(text) {
    return String(text).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /**
   * Engraving size, the way the engravers lay it out. Text typed on one line wraps over as many lines as it
   * needs at the largest size where every word fits ("Happy 14th / Anniversary / xxx"). When the shopper
   * breaks the lines themselves, each line stays on one line, sized so the longest just fits the width; a
   * very long line stops shrinking at 45% of the maximum and wraps instead. Never above the shape's maximum,
   * then step down until the block fits the height.
   * `measure(text, px)` → rendered width; `fitsHeight(px)` → whether the block fits its box.
   */
  function fitFontSize(text, box, measure, fitsHeight) {
    const words = String(text).split(/\s+/).filter(Boolean);
    const target = box.width * 0.98;
    const ownLines = String(text).includes('\n');
    const pieces = ownLines ? String(text).split('\n').map(line => line.trim()) : words;
    const widest = Math.max(0, ...pieces.map(piece => measure(piece, box.fmax)));
    let size = widest > target ? Math.max(ownLines ? box.fmax * 0.45 : 0, box.fmax * target / widest) : box.fmax;
    const wordsFit = px => words.every(word => measure(word, px) <= target + 0.01); // allow for rounding
    while (size > 7 && (!wordsFit(size) || !fitsHeight(size))) size = Math.max(7, size - 0.5);
    return Math.max(7, size);
  }

  /**
   * The engraving's lines as the preview lays them out (white-space: pre-line, text-wrap: balance): the shopper's own
   * line breaks kept, then each of their lines wrapped at the narrowest width that needs no more lines than full width.
   * `measure(text)` → rendered width. Used to draw the engraving onto the plaque photo for the order.
   */
  function balancedLines(text, maxWidth, measure) {
    const wrap = (words, width) => words.reduce((lines, word) => {
      const last = lines[lines.length - 1];
      if (last !== undefined && measure(last + ' ' + word) <= width) lines[lines.length - 1] = last + ' ' + word;
      else lines.push(word);
      return lines;
    }, []);
    return String(text).split('\n').reduce((all, own) => {
      const words = own.split(/\s+/).filter(Boolean);
      if (!words.length) return all.concat('');
      const count = wrap(words, maxWidth).length;
      let low = 0;
      let high = maxWidth;
      for (let i = 0; i < 14; i++) {
        const mid = (low + high) / 2;
        if (wrap(words, mid).length <= count) high = mid; else low = mid;
      }
      return all.concat(wrap(words, high));
    }, []);
  }

  /** Two words typed on one line go one per line ("Love you" → "Love" / "you"); anything else stays as typed. */
  function twoWordLines(text) {
    const value = String(text);
    if (value.includes('\n')) return value; // the shopper's own line breaks win
    const words = value.trim().split(/\s+/).filter(Boolean);
    return words.length === 2 ? words.join('\n') : value;
  }

  /** Keep at most `max` lines; anything after the last allowed line joins it with spaces. */
  function limitLines(text, max) {
    const lines = String(text).split('\n');
    if (lines.length <= max) return String(text);
    return lines.slice(0, max - 1).concat(lines.slice(max - 1).join(' ')).join('\n');
  }

  const WEEKDAY_KEYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  /** The store's calendar date at `epochMs` as { y, m, d } (m is 1-12). */
  function storeDate(epochMs, timeZone, fallbackOffsetMinutes) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(epochMs));
      const get = type => Number(parts.find(p => p.type === type).value);
      return { y: get('year'), m: get('month'), d: get('day') };
    } catch (e) {
      const shifted = new Date(epochMs + (Number(fallbackOffsetMinutes) || 0) * 60000);
      return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth() + 1, d: shifted.getUTCDate() };
    }
  }

  function shiftDate(date, days) {
    const t = new Date(Date.UTC(date.y, date.m - 1, date.d + days));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  }

  function isoDate(date) {
    return date.y + '-' + String(date.m).padStart(2, '0') + '-' + String(date.d).padStart(2, '0');
  }

  function weekdayKey(date) {
    return WEEKDAY_KEYS[new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay()];
  }

  /**
   * Buunto's order cut-off and the first delivery date it unlocks, worked out from its settings.
   * Like Buunto, the cut-off is the last one that still gets that first delivery date (on a Friday afternoon,
   * Sunday's cut-off still gets Monday when Sunday is closed).
   * Used until Buunto's own getCutOffTime() has data (that needs its picker rendered on the page).
   * Returns { at: epoch ms, day: "YYYY-MM-DD" }, or null when no day is open.
   */
  function buuntoCutOff(nowMs, settings) {
    const s = settings || {};
    const byDay = s.cutOffByDay || {};
    const openDays = s.availableWeekDays || WEEKDAY_KEYS.slice(1);
    const closedDates = s.disabledDates || [];
    const ruleFor = date => byDay[weekdayKey(date)] || byDay.DEFAULT || s;
    const horizonFor = date => Number(ruleFor(date).lastAvailableDateInDays || s.lastAvailableDateInDays || 90);
    const cutOffOn = date => {
      const time = String(ruleFor(date).cutOffTime || s.cutOffTime || '23:59');
      return storeTimeToEpoch(isoDate(date) + ' ' + (time.length === 5 ? time + ':00' : time), s.timezoneId, s.timezoneOffsetMinutes);
    };
    /** The first open delivery day for an order placed before `cutDay`'s cut-off, as "YYYY-MM-DD", or null. */
    const firstOpenDay = cutDay => {
      const rule = ruleFor(cutDay);
      const leadDays = Number(rule.firstAvailableDateInDays != null ? rule.firstAvailableDateInDays : (s.firstAvailableDateInDays || 0));
      const horizon = horizonFor(cutDay);
      let day = shiftDate(cutDay, leadDays);
      for (let i = 0; i <= horizon; i++) {
        if (openDays.indexOf(weekdayKey(day)) !== -1 && closedDates.indexOf(isoDate(day)) === -1) return isoDate(day);
        day = shiftDate(day, 1);
      }
      return null;
    };

    let cutDay = storeDate(nowMs, s.timezoneId, s.timezoneOffsetMinutes);
    let at = cutOffOn(cutDay);
    if (!(nowMs < at)) { cutDay = shiftDate(cutDay, 1); at = cutOffOn(cutDay); }

    const day = firstOpenDay(cutDay);
    if (!day) return null;
    // Later cut-offs that still get the same first delivery day: order by the last of them.
    const horizon = horizonFor(cutDay);
    for (let i = 0; i < horizon; i++) {
      const laterDay = shiftDate(cutDay, 1);
      if (firstOpenDay(laterDay) !== day) break;
      cutDay = laterDay;
      at = cutOffOn(cutDay);
    }
    return { at: at, day: day };
  }

  /* ---------- Delivery options split test (TSK-11502, run by Eraya) ---------- */

  /** The Eraya variation ("a", "b") this visitor has for `testId`, from its `eraya_experiences` value, or null. */
  function erayaVariation(raw, testId) {
    try {
      const map = JSON.parse(raw || '{}');
      return map && typeof map[testId] === 'string' ? map[testId] : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * The variation Eraya assigned this visitor for a shipping test, from localStorage.eraya_shipping_test (a list of
   * {testId, activeVariation}). Eraya's preview link sets it here, and not in eraya_experiences.
   */
  function erayaShippingVariation(raw, testId) {
    try {
      const list = JSON.parse(raw || '[]');
      const entry = Array.isArray(list) ? list.find(item => item && item.testId === testId) : null;
      return entry && typeof entry.activeVariation === 'string' ? entry.activeVariation : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * The test's delivery prices in pence, from the rates Eraya gives that variation at checkout (so the steps and
   * checkout can't disagree). Rates are matched by name: "SPECIAL", "PREMIUM", "FREE", or the first test's "NEXT DAY"
   * and "STANDARD". Missing ones are left out.
   */
  function erayaRates(meta, testId, variationId) {
    const tests = (meta && meta.shippingTests && meta.shippingTests.tests) || [];
    const test = tests.find(t => t._id === testId);
    const variation = test && (test.shippingVariations || []).find(v => v.variationId === variationId);
    const prices = {};
    ((variation && variation.customRates) || []).forEach(rate => {
      const key = { SPECIAL: 'next', 'NEXT DAY': 'next', PREMIUM: 'premium', FREE: 'standard', STANDARD: 'standard' }[String(rate.name || '').trim().toUpperCase()];
      if (key && !(key in prices) && isFinite(rate.price)) prices[key] = Math.round(Number(rate.price) * 100);
    });
    return prices;
  }

  /** A delivery option's price as shown on its card: "FREE" for nothing, otherwise "£3" or "£3.50". */
  function deliveryPrice(cents, format) {
    return Number(cents) === 0 ? 'FREE' : formatMoney(cents, format, true);
  }

  /** The line under the order total saying which delivery it includes, and at what price unless it's free. */
  function deliveryNote(name, cents, format) {
    return 'Includes ' + name + ' delivery' + (Number(cents) === 0 ? '' : ', ' + formatMoney(cents, format, true));
  }

  /**
   * The test's three delivery options at `nowMs`, on the store's calendar and open days from Buunto's settings:
   * - nextDay: the first day Buunto's cut-off still gets, so tomorrow until the cut-off and later after it; null if none
   * - nextDayIsTomorrow: whether nextDay is tomorrow, so the card only promises "tomorrow" when it is
   * - standard: no date choice; a window starting 2 days ahead (3 after midday), on the first open day, running 3 open days
   * - premium: any open day from the Standard window's start (as in the mockup) up to 90 days ahead
   * All dates "YYYY-MM-DD".
   */
  function deliveryOptions(nowMs, settings) {
    const s = settings || {};
    const openDays = s.availableWeekDays || WEEKDAY_KEYS.slice(1);
    const closedDates = s.disabledDates || [];
    const isOpen = date => openDays.indexOf(weekdayKey(date)) !== -1 && closedDates.indexOf(isoDate(date)) === -1;
    const nextOpen = date => {
      for (let i = 0; i < 60; i++, date = shiftDate(date, 1)) if (isOpen(date)) return date;
      return null;
    };

    const today = storeDate(nowMs, s.timezoneId, s.timezoneOffsetMinutes);
    const midday = storeTimeToEpoch(isoDate(today) + ' 12:00:00', s.timezoneId, s.timezoneOffsetMinutes);
    const from = nextOpen(shiftDate(today, nowMs < midday ? 2 : 3));
    let to = from;
    for (let more = 2; to && more > 0; more--) to = nextOpen(shiftDate(to, 1));

    const tomorrow = isoDate(shiftDate(today, 1));
    const cutOff = buuntoCutOff(nowMs, s);
    return {
      nextDay: cutOff ? cutOff.day : null,
      nextDayIsTomorrow: !!cutOff && cutOff.day === tomorrow,
      standard: from && to ? { from: isoDate(from), to: isoDate(to) } : null,
      premium: from ? { from: isoDate(from), to: isoDate(shiftDate(today, 90)), closedDates: closedDates, openDays: openDays } : null
    };
  }

  /** Whether `day` ("YYYY-MM-DD") can be picked for Premium in `premium` (from deliveryOptions). */
  function premiumDayOpen(day, premium) {
    if (!premium || !/^\d{4}-\d{2}-\d{2}$/.test(String(day)) || day < premium.from || day > premium.to) return false;
    const [y, m, d] = day.split('-').map(Number);
    return premium.openDays.indexOf(weekdayKey({ y, m, d })) !== -1 && premium.closedDates.indexOf(day) === -1;
  }

  /** "2026-10-08" → "Thu 08 Oct 2026", the way Buunto writes the "Delivery Date" attribute that orders and packing slips read. */
  function buuntoDateText(day) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
    return m ? weekdayOf(day, true) + ' ' + m[3] + ' ' + MONTHS[+m[2] - 1].slice(0, 3) + ' ' + m[1] : '';
  }

  /** "Thu 08 Oct 2026" (Buunto's "Delivery Date") → "2026-10-08", or null. */
  function parseBuuntoDate(text) {
    const m = /^[A-Za-z]{3} (\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec(String(text || '').trim());
    const month = m ? MONTHS.findIndex(name => name.slice(0, 3) === m[2]) : -1;
    return month === -1 ? null : m[3] + '-' + String(month + 1).padStart(2, '0') + '-' + m[1].padStart(2, '0');
  }

  /**
   * The saved choice, if it still holds at `options` (from deliveryOptions). `saved` holds the cart's "Delivery Option"
   * and "Delivery Date". Standard has no date to go stale (its window just moves on); a Next day or Premium date that
   * can no longer be had is `stale`, so the shopper is asked to choose again rather than having it changed for them.
   * The old names are still read, for carts saved before the renames.
   * Returns { choice, date, stale }.
   */
  function restoreDelivery(saved, options) {
    const choice = {
      Special: 'next', Premium: 'premium', Free: 'standard',
      'Special Guaranteed Delivery': 'next', 'Premium Delivery': 'premium', 'Standard Delivery': 'standard',
      'Next day': 'next', Standard: 'standard'
    }[saved && saved.option];
    const day = parseBuuntoDate(saved && saved.date);
    if (choice === 'standard' && options.standard) return { choice: 'standard', date: null, stale: false };
    if (choice === 'next' && options.nextDay && day === options.nextDay) return { choice: 'next', date: day, stale: false };
    if (choice === 'premium' && premiumDayOpen(day, options.premium)) return { choice: 'premium', date: day, stale: false };
    return { choice: null, date: null, stale: !!choice };
  }

  /** The gift steps in order. Garden extras only when there is a hamper in this gift to add them to. */
  function giftSteps(opts) {
    const hasHamper = opts.hamperProduct || opts.hamperChosen;
    return ['message'].concat(opts.plaque ? ['plaque'] : [], opts.addons && hasHamper ? ['addons'] : [], ['date']);
  }

  /** Whether two cart lines carry the same properties; a blank property counts as no property. */
  function sameProperties(a, b) {
    const filled = props => Object.keys(props || {}).filter(key => String(props[key] == null ? '' : props[key]) !== '').sort();
    const keys = filled(a);
    const other = filled(b);
    return keys.length === other.length && keys.every((key, i) => key === other[i] && String(a[key]) === String(b[key]));
  }

  const core = { formatMoney, storeTimeToEpoch, formatCountdown, weekdayOf, longDateOf, findVariant, lineForKey, joinList, handleize, fitFontSize, balancedLines, twoWordLines, limitLines, buuntoCutOff, giftSteps, erayaVariation, erayaShippingVariation, erayaRates, deliveryPrice, deliveryNote, deliveryOptions, premiumDayOpen, buuntoDateText, parseBuuntoDate, restoreDelivery, sameProperties };
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  window.PDP3Core = core; // the delivery options test (assets/delivery-options.js) shares these, on the cart too

  /* ================= Browser ================= */

  function initPDP3(root) {
    const config = JSON.parse(root.querySelector('[data-pdp3-config]').textContent);
    const $ = selector => root.querySelector(selector);
    const $$ = selector => Array.from(root.querySelectorAll(selector));
    const on = (selector, type, handler) => { const el = $(selector); if (el) el.addEventListener(type, handler); return el; };
    // This store's money format is HTML ("<span class=money>£{{amount}}</span>"); prices are set with textContent, so drop the tags.
    const moneyFormat = String(config.moneyFormat || '').replace(/<[^>]*>/g, '');
    const money = (cents, trim) => formatMoney(cents, moneyFormat, trim);
    const state = {
      variant: config.variants.find(v => v.id === config.selectedVariantId) || config.variants[0],
      gift: 'tree'
    };

    /* ---------- Carousels (gallery and plaque photos) ---------- */
    const reduceMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    /** Swipeable track with tappable dots and, optionally, prev/next buttons (data-step="-1" / "1") that wrap round at the ends. */
    function carousel(track, dotsEl, onChange, arrows) {
      if (!track) return { slides: [], show() {} };
      const slides = Array.from(track.children);
      let current = 0;
      function show(index, smooth) {
        if (index < 0 || index >= slides.length) return;
        track.scrollTo({ left: index * track.clientWidth, behavior: smooth === false || reduceMotion() ? 'auto' : 'smooth' });
        mark(index);
      }
      if (dotsEl) {
        dotsEl.textContent = '';
        if (slides.length > 1) slides.forEach((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + slides.length);
          dot.addEventListener('click', () => show(i));
          dotsEl.append(dot);
        });
      }
      const dots = dotsEl ? Array.from(dotsEl.children) : [];
      (arrows || []).forEach(btn => {
        btn.hidden = slides.length < 2;
        btn.addEventListener('click', () => show((current + Number(btn.dataset.step) + slides.length) % slides.length));
      });
      function mark(index) {
        current = index;
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-on', i === index);
          dot.setAttribute('aria-current', String(i === index));
        });
        if (onChange) onChange(index);
      }
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; mark(Math.round(track.scrollLeft / (track.clientWidth || 1))); });
      }, { passive: true });
      mark(0);
      return { slides, show };
    }

    const thumbs = $$('.pdp3-gallery__thumb');
    const galleryTrack = $('[data-pdp3-track]');
    const galleryStage = $('.pdp3-gallery__stage');
    let shownSlide = -1;
    const gallery = carousel(galleryTrack, $('[data-pdp3-dots]'), index => {
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
      // Videos play only while on show; act on a change of slide only, so a shopper's own pause sticks
      if (index === shownSlide) return;
      shownSlide = index;
      Array.from(galleryTrack.children).forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i !== index) video.pause();
        else if (!reduceMotion()) video.play().catch(() => {});
      });
      const slide = galleryTrack.children[index];
      if (galleryStage) galleryStage.classList.toggle('is-video', !!(slide && slide.querySelector('video')));
    }, $$('[data-pdp3-gallery-arrow]'));
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => gallery.show(i)));

    function showMedia(mediaId, smooth) {
      if (!mediaId) return;
      const index = gallery.slides.findIndex(slide => slide.dataset.mediaId === String(mediaId));
      if (index !== -1) gallery.show(index, smooth);
    }

    /* ---------- Options, prices and gift choice ---------- */
    const optionGroups = $$('[data-pdp3-option]');
    const giftField = $('[data-pdp3-gift-field]');
    const ctaWrap = $('[data-pdp3-cta]');
    const oosWrap = $('[data-pdp3-oos]');
    const sticky = $('[data-pdp3-sticky]');
    const bisTrigger = root.querySelector('#klaviyo-bis-trigger');
    const sendError = $('[data-pdp3-send-error]');

    function showSendError(message) { if (!sendError) return; sendError.textContent = message; sendError.hidden = false; }
    function hideSendError() { if (sendError) sendError.hidden = true; }

    function selectedOptions() {
      return optionGroups.map(group => {
        const pressed = group.querySelector('[aria-pressed="true"]');
        return pressed ? pressed.dataset.value : null;
      });
    }

    function giftTotal() {
      return state.variant.price + (state.gift === 'hamper' && config.hamper ? config.hamper.price : 0);
    }

    function renderPrices() {
      const total = money(giftTotal());
      $$('[data-pdp3-price], [data-pdp3-total]').forEach(el => { el.textContent = total; });
      const treePrice = $('[data-pdp3-tree-price]');
      if (treePrice) treePrice.textContent = money(state.variant.price, true);
      const hamperPrice = $('[data-pdp3-hamper-price]');
      if (hamperPrice && config.hamper) hamperPrice.textContent = money(state.variant.price + config.hamper.price, true);
    }

    function renderAvailability() {
      const chosen = selectedOptions();
      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('.pdp3-seg__btn').forEach(btn => {
          const options = chosen.slice();
          options[groupIndex] = btn.dataset.value;
          const match = findVariant(config.variants, options);
          const soldOut = !match || !match.available;
          btn.classList.toggle('is-soldout', soldOut);
          const sub = btn.querySelector('[data-pdp3-sub]');
          if (sub) sub.textContent = soldOut ? 'Out of stock' : btn.dataset.sub;
        });
      });
      const available = state.variant.available;
      if (giftField) giftField.hidden = !available; // no gift choice for a size that can't be bought
      if (ctaWrap) ctaWrap.hidden = !available;
      if (oosWrap) oosWrap.hidden = available;
      if (sticky) sticky.hidden = !available;
      if (bisTrigger) bisTrigger.setAttribute('data-variant-id', state.variant.id);
    }

    function renderGallery() {
      if (state.gift === 'hamper' && config.hamper && state.variant.hamperMediaId) showMedia(state.variant.hamperMediaId);
      else showMedia(state.variant.mediaId);
    }

    /** The gift cards' photos follow the chosen size: the tree's own photo, and its hamper photo for that size. */
    function renderGiftImages() {
      [['[data-pdp3-tree-img] img', state.variant.image], ['[data-pdp3-hamper-img] img', state.variant.hamperImage]].forEach(([selector, src]) => {
        const img = root.querySelector(selector);
        if (img && src) { img.removeAttribute('srcset'); img.src = src; }
      });
    }

    optionGroups.forEach(group => {
      group.addEventListener('click', event => {
        const btn = event.target.closest('.pdp3-seg__btn');
        if (!btn) return;
        const groupIndex = optionGroups.indexOf(group);
        const wanted = selectedOptions();
        wanted[groupIndex] = btn.dataset.value;
        // Exact match first; otherwise the nearest variant that has the clicked value (in stock preferred).
        const sameValue = config.variants.filter(v => v.options[groupIndex] === btn.dataset.value);
        const match = findVariant(config.variants, wanted) || sameValue.find(v => v.available) || sameValue[0];
        if (!match) return;
        // Keep every option group in step with the chosen variant, so the buttons never disagree with the price and button.
        optionGroups.forEach((g, i) => g.querySelectorAll('.pdp3-seg__btn').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.value === match.options[i]))));
        state.variant = match;
        hideSendError();
        renderPrices();
        renderAvailability();
        renderGiftImages();
        renderGallery();
      });
    });

    const giftButtons = $$('[data-pdp3-gift]');
    giftButtons.forEach(btn => btn.addEventListener('click', () => {
      state.gift = btn.dataset.pdp3Gift;
      giftButtons.forEach(b => { const isOn = b === btn; b.setAttribute('aria-pressed', String(isOn)); b.classList.toggle('is-on', isOn); });
      const inside = $('[data-pdp3-inside]');
      if (inside) inside.hidden = state.gift !== 'hamper';
      renderPrices();
      renderGallery();
    }));

    /* ---------- Hamper info sheets ---------- */
    $$('[data-pdp3-open-sheet]').forEach(btn => btn.addEventListener('click', () => {
      const dialog = document.getElementById(btn.dataset.pdp3OpenSheet);
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    }));
    $$('.pdp3-sheet').forEach(dialog => dialog.addEventListener('click', event => {
      if (event.target === dialog || event.target.closest('[data-pdp3-close-sheet]')) dialog.close();
    }));

    /* ---------- Feefo rating ---------- */
    const rating = $('[data-pdp3-rating]');
    if (rating) {
      fetch('https://api.feefo.com/api/10/reviews/summary/product?parent_product_sku=' + config.productId +
            '&merchant_identifier=the-present-tree-ltd&since_period=ALL&importedReviews=true')
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          const count = data && data.meta ? Number(data.meta.count) : 0;
          const score = data && data.rating ? Number(data.rating.rating) : 0;
          if (!count) return;
          const stars = $('[data-pdp3-stars]');
          stars.style.setProperty('--pdp3-rating', (Math.max(0, Math.min(5, score)) / 5) * 100 + '%');
          stars.setAttribute('aria-label', 'Rated ' + score.toFixed(1) + ' out of 5');
          $('[data-pdp3-rating-count]').textContent = count + (count === 1 ? ' review' : ' reviews');
          rating.hidden = false;
        })
        .catch(() => {});
    }

    /* ---------- Order-by countdown, from the Buunto date picker's own cut-off ---------- */
    function waitForBuunto(timeoutMs) {
      return new Promise(resolve => {
        const started = Date.now();
        (function poll() {
          const picker = window.Buunto && window.Buunto.datePicker;
          if (picker && typeof picker.getCutOffTime === 'function' && picker.settings) { resolve(picker); return; }
          if (Date.now() - started > timeoutMs) { resolve(null); return; }
          setTimeout(poll, 250);
        })();
      });
    }

    /** The next cut-off and the first delivery date it gets: Buunto's own once its picker has rendered, else from its settings. */
    function readCutOff(picker) {
      try {
        const info = picker.getCutOffTime();
        if (info && info.firstAvailableDate && info.cutOffTime) {
          return {
            at: storeTimeToEpoch(info.cutOffTime, picker.settings.timezoneId, picker.settings.timezoneOffsetMinutes),
            day: info.firstAvailableDate
          };
        }
      } catch (e) { /* fall through to the settings calculation */ }
      return buuntoCutOff(Date.now(), picker.settings);
    }

    const nudge = $('[data-pdp3-nudge]');
    const stickyDay = $('[data-pdp3-sticky-day]');
    waitForBuunto(10000).then(picker => {
      if (!picker || !nudge) return;
      let cutOff = null;
      function tick() {
        if (!cutOff || !(cutOff.at - Date.now() > 0)) cutOff = readCutOff(picker);
        const left = cutOff ? formatCountdown(cutOff.at - Date.now()) : null;
        if (!left) {
          nudge.hidden = true;
          if (stickyDay) stickyDay.hidden = true;
          return;
        }
        const text = $('[data-pdp3-nudge-text]');
        const strong = document.createElement('b');
        strong.textContent = left;
        text.textContent = '';
        text.append('You choose the day. Order within ', strong, ' for delivery from ' + longDateOf(cutOff.day));
        nudge.hidden = false;
        if (stickyDay) { stickyDay.textContent = 'Delivery from ' + longDateOf(cutOff.day, true); stickyDay.hidden = false; }
      }
      tick();
      setInterval(tick, 30000);
    });

    /* ---------- Sticky bar: only while the main button is off screen ---------- */
    let stickyShown = null; // null until the observer first reports
    let pageInert = null; // the elements the open steps made inert; null while the steps are closed
    function syncStickyInert() {
      if (sticky && stickyShown !== null && !pageInert) sticky.inert = !stickyShown; // while the steps are open the page stays inert
    }
    if (sticky && ctaWrap && 'IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        stickyShown = !entries[0].isIntersecting;
        sticky.classList.toggle('is-visible', stickyShown);
        syncStickyInert();
      }).observe(ctaWrap);
    }

    renderPrices();
    renderAvailability();
    showMedia(state.variant.mediaId, false);

    /* ---------- Cart (through CartJS, so the rest of the theme stays in sync) ---------- */
    const DATE_ATTRIBUTE = 'Delivery Date'; // Buunto writes "Delivery Date" and "Delivery Day" cart attributes

    function cartRequest(run) {
      return new Promise((resolve, reject) => {
        if (!window.CartJS) { reject(new Error('Cart unavailable')); return; }
        run({ success: data => resolve(data), error: xhr => reject(xhr) });
      });
    }
    function refreshCart() { return cartRequest(opts => window.CartJS.getCart(opts)); }
    function addLines(items) {
      return cartRequest(opts => window.CartJS.addItems(items, opts)).then(res => refreshCart().then(cart => ({ res, cart })));
    }
    /**
     * Change a cart line by its line item key (/cart/change.js takes a key as `id`), never by position.
     * Leave `properties` out for a quantity-only change: Shopify keeps the line's properties when none are sent.
     * Resolves with the cart. A property change gives the line a new key.
     */
    function changeLine(key, quantity, properties) {
      return cartRequest(opts => window.CartJS.updateItemById(key, quantity, properties || {}, opts));
    }
    function cachedItem(key) {
      const items = window.CartJS && window.CartJS.cart && window.CartJS.cart.items;
      return (items || []).find(item => item.key === key) || null;
    }
    /** Take one off a line (lines merge, so never assume our quantity is the whole line). */
    function decrementLine(key) {
      const item = cachedItem(key);
      if (!item) return Promise.resolve(null);
      return changeLine(key, Math.max(0, item.quantity - 1));
    }
    function errorMessage(err) {
      const json = err && err.responseJSON;
      return (json && (json.description || json.message)) || 'Sorry, something went wrong. Please try again.';
    }
    let basketTotal = null; // pence, from the last cart we saw
    function onCartChanged(cart) {
      if (!cart) return;
      document.querySelectorAll('.basket-number_amount').forEach(el => { el.textContent = cart.item_count; });
      basketTotal = cart.total_price;
      renderOrderTotal();
    }
    /** The basket total, plus the chosen delivery on the delivery step of the delivery options test. */
    function renderOrderTotal() {
      const total = $('[data-pdp3-order-total]');
      const note = $('[data-pdp3-total-note]');
      const delivery = flow.open && flow.step === 'date' && deliveryTestOn() && deliveryEl.choice ? deliveryEl : null;
      if (total && basketTotal !== null) total.textContent = money(basketTotal + (delivery ? delivery.price : 0));
      if (note) {
        note.hidden = !delivery;
        if (delivery) note.textContent = deliveryNote(delivery.choiceName, delivery.price, moneyFormat);
      }
    }

    /* ---------- Send this gift ---------- */
    const overlay = $('[data-pdp3-steps]');
    // The garden extras info pop-up is position: fixed inside the scrolling step area, and iOS scrolls that area instead
    // of the pop-up. Move it up to the overlay itself (the grid's script has already bound its listeners, so they come too).
    const addonModal = overlay && overlay.querySelector('.pdp3-addons .addon-info-modal');
    if (addonModal) overlay.append(addonModal);
    // engraving: the words for a tree's included plaque (config.plaque.included), kept on the tree's own line;
    // preview: the link Shopify gave the picture of those words on the plaque photo
    const flow = { treeKey: null, hamperKey: null, plaqueKey: null, variantId: null, hamper: false, message: '', engraving: '', preview: '', plaque: null, datePreselected: false, open: false, step: null };
    const includedPlaque = !!(config.plaque && config.plaque.included);
    // Same structure as the £15 plaque's line: the words and their picture shown to the shopper, the rest hidden (a leading _)
    const PREVIEW_PROPERTY = 'Engraving Image';
    /** An included plaque's engraving properties; blank values when there is no engraving, so a save clears them. */
    function engravingProperties(engraving, preview) {
      return {
        'Engraving': engraving,
        '_Engraving Type': engraving ? 'Included plaque' : '',
        '_For Product': engraving ? config.productTitle : '',
        [PREVIEW_PROPERTY]: engraving ? preview : ''
      };
    }
    /** The tree line's properties for what the shopper has written so far. */
    function treeProperties() {
      const props = {};
      if (flow.message) props['Gift Message'] = flow.message;
      if (flow.engraving) {
        const engraving = engravingProperties(flow.engraving, flow.preview);
        Object.keys(engraving).forEach(key => { if (engraving[key]) props[key] = engraving[key]; });
      }
      return props;
    }
    /** Worked out each time the steps open: garden extras only come with a hamper. */
    const currentSteps = () => giftSteps({ plaque: !!config.plaque, addons: !!config.addons, hamperProduct: !!config.hamperProduct, hamperChosen: flow.hamper });
    let steps = currentSteps();

    function setSendBusy(busy) {
      $$('[data-pdp3-send]').forEach(btn => { btn.disabled = busy; btn.setAttribute('aria-busy', String(busy)); });
    }

    /** The shopper may have pressed the sticky bar, so bring the error under the main button into view. */
    function showSendFailure(message) {
      showSendError(message);
      const errorEl = $('[data-pdp3-send-error]');
      if (errorEl) errorEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    $$('[data-pdp3-send]').forEach(btn => btn.addEventListener('click', onSend));

    function onSend(event) {
      const trigger = event && event.currentTarget; // focus goes back here on close (Safari doesn't focus a tapped button)
      hideSendError();
      const toast = $('[data-pdp3-toast]');
      if (toast) toast.hidden = true; // "... is in your basket" belongs to the last gift
      if (!state.variant.available) return;
      if (!config.stepsEnabled || !overlay) {
        setSendBusy(true);
        addLines([{ id: state.variant.id, quantity: 1 }])
          .then(() => { window.location.href = '/cart'; })
          .catch(err => { setSendBusy(false); showSendFailure(errorMessage(err)); });
        return;
      }
      const wantsHamper = state.gift === 'hamper' && !!config.hamper;
      const variantChanged = !flow.treeKey || flow.variantId !== state.variant.id;
      const hamperChanged = wantsHamper !== flow.hamper || (wantsHamper && !flow.hamperKey);
      if (!variantChanged && !hamperChanged) { openSteps(trigger); return; }

      setSendBusy(true);
      let chain = Promise.resolve();
      if (variantChanged && flow.treeKey) chain = chain.then(() => decrementLine(flow.treeKey));
      if (hamperChanged && flow.hamperKey) chain = chain.then(() => decrementLine(flow.hamperKey));
      chain
        .then(() => {
          const items = [];
          if (variantChanged) items.push({ id: state.variant.id, quantity: 1, properties: treeProperties() });
          if (wantsHamper && hamperChanged) items.push({ id: config.hamper.variantId, quantity: 1 });
          // Only the hamper came off (back to tree only): nothing to add, and /cart/add.js rejects an empty list.
          return items.length ? addLines(items) : refreshCart().then(cart => ({ res: null, cart }));
        })
        .then(({ res, cart }) => {
          const added = (res && res.items) || [];
          if (variantChanged) {
            const tree = added.find(item => item.variant_id === state.variant.id);
            flow.treeKey = tree ? tree.key : null;
            flow.variantId = state.variant.id;
          }
          if (hamperChanged) {
            const hamperLine = wantsHamper ? added.find(item => item.variant_id === config.hamper.variantId) : null;
            flow.hamperKey = hamperLine ? hamperLine.key : null;
          }
          flow.hamper = wantsHamper;
          setSendBusy(false);
          onCartChanged(cart);
          openSteps(trigger);
        })
        .catch(err => {
          if (variantChanged) { flow.treeKey = null; flow.variantId = null; }
          if (hamperChanged) { flow.hamperKey = null; flow.hamper = false; }
          setSendBusy(false);
          showSendFailure(errorMessage(err));
          refreshCart().then(onCartChanged).catch(() => {});
        });
    }

    /* ---------- Open, close and navigate (each step is a history entry, so the phone's back button works) ---------- */
    let lastFocus = null;
    let pendingClose = null;
    if (history.state && history.state.pdp3) history.replaceState(null, '');

    /**
     * While the steps are open nothing behind them can take focus (aria-modal alone doesn't stop Tab): every sibling of
     * the overlay and of each of its ancestors up to <body> is made inert. Only the elements set here are released on
     * close; ones that were already inert stay as they were.
     */
    function setPageInert(on) {
      if (on) {
        if (pageInert) return;
        pageInert = [];
        for (let el = overlay; el.parentElement && el !== document.body; el = el.parentElement) {
          Array.from(el.parentElement.children).forEach(sibling => {
            if (sibling === el || sibling.inert) return;
            sibling.inert = true;
            pageInert.push(sibling);
          });
        }
      } else if (pageInert) {
        pageInert.forEach(el => { el.inert = false; });
        pageInert = null;
        syncStickyInert(); // the sticky bar may have come or gone while the steps were open
      }
    }

    function openSteps(trigger) {
      steps = currentSteps();
      flow.open = true;
      lastFocus = trigger || $('.pdp3-cta [data-pdp3-send]') || document.activeElement;
      overlay.hidden = false;
      document.documentElement.classList.add('pdp3-lock');
      setPageInert(true);
      trackFlowStart();
      goTo('message', true);
    }

    function goTo(name, push) {
      if (push) {
        const depth = history.state && history.state.pdp3 ? history.state.depth : 0;
        history.pushState({ pdp3: name, depth: depth + 1 }, '');
      }
      showStep(name);
    }

    function nextStep() {
      if (!flow.open) return; // a save finished after the steps were closed
      const index = steps.indexOf(flow.step);
      if (index < steps.length - 1) goTo(steps[index + 1], true);
    }

    function showStep(name) {
      flow.step = name;
      const index = steps.indexOf(name);
      $$('[data-pdp3-step]').forEach(el => { el.hidden = el.dataset.pdp3Step !== name; });
      $$('[data-pdp3-actions]').forEach(el => { el.hidden = el.dataset.pdp3Actions !== name; });
      $('[data-pdp3-step-count]').textContent = 'Step ' + (index + 1) + ' of ' + steps.length;
      $('[data-pdp3-progress]').innerHTML = steps.map((s, i) => '<i class="' + (i <= index ? 'is-on' : '') + '"></i>').join('');
      hideStepError();
      const scroller = $('[data-pdp3-steps-scroll]');
      if (scroller) scroller.scrollTop = 0;
      if (name === 'message') renderMessage();
      if (name === 'plaque') renderPlaque();
      if (name === 'addons') renderAddons();
      if (name === 'date') onDateStep();
      renderOrderTotal(); // delivery is only added on the delivery step
      const title = root.querySelector('[data-pdp3-step="' + name + '"] .pdp3-step__title');
      if (title) title.focus({ preventScroll: true });
      trackStep(name, index);
    }

    function closeSteps(reason) {
      flow.open = false;
      pendingClose = null;
      overlay.hidden = true;
      document.documentElement.classList.remove('pdp3-lock');
      setPageInert(false); // before restoreFocus: an inert element can't take focus
      trackFlowEnd(reason);
      // The sticky bar is inert while the main button is on screen: if the steps were opened from it, focus the main button.
      restoreFocus(lastFocus && lastFocus.closest && lastFocus.closest('[inert]') ? $('.pdp3-cta [data-pdp3-send]') : lastFocus);
    }

    function restoreFocus(el) {
      if (!el || typeof el.focus !== 'function') return;
      el.focus({ preventScroll: false });
      // About a second after a Back (popstate) Buunto's button sweep disables every button on the page (see "Buunto's button sweep"),
      // which can drop focus. Put it back once the button is enabled again.
      if (el.tagName !== 'BUTTON' || !('MutationObserver' in window)) return;
      const watcher = new MutationObserver(() => {
        if (!el.disabled && document.activeElement === document.body) el.focus({ preventScroll: true });
      });
      watcher.observe(el, { attributes: true, attributeFilter: ['disabled'] });
      setTimeout(() => watcher.disconnect(), 4000);
    }

    /** Close from code: unwind the history entries the steps pushed, then close on popstate. */
    function leaveSteps(reason) {
      const depth = history.state && history.state.pdp3 ? history.state.depth : 0;
      if (depth > 0) { pendingClose = reason; history.go(-depth); } else { closeSteps(reason); }
    }

    window.addEventListener('popstate', event => {
      if (!flow.open) return;
      const name = event.state && event.state.pdp3;
      if (name && steps.indexOf(name) !== -1) { showStep(name); return; }
      closeSteps(pendingClose || 'close_button');
    });
    on('[data-pdp3-back]', 'click', () => history.back());
    // On the document, so Escape still goes back when focus has dropped to <body>. Buunto's picker handles its own Escape,
    // and so does the garden extras info pop-up: capture phase, so this runs before the pop-up's own listener closes it.
    document.addEventListener('keydown', event => {
      if (!flow.open || event.key !== 'Escape') return;
      if (event.target instanceof Element && event.target.closest('#buunto-date-picker')) return;
      if (root.querySelector('.addon-info-modal.open')) return;
      event.preventDefault();
      history.back();
    }, true);

    const stepError = $('[data-pdp3-step-error]');
    function showStepError(message) { if (!stepError) return; stepError.textContent = message; stepError.hidden = false; }
    function hideStepError() { if (stepError) stepError.hidden = true; }
    function setBusy(btn, busy) { if (!btn) return; btn.disabled = busy; btn.setAttribute('aria-busy', String(busy)); }

    /* ---------- Buunto's button sweep ----------
       Buunto re-syncs after some cart writes: about a second after every Back (the upsell block writes cart attributes on
       popstate) and sometimes just after Send. While it does, it disables every enabled button on the page for about 0.2-0.5s,
       then re-enables every disabled one. That swallows taps on our buttons and clears our busy states mid-request
       (a second tap on Send could add a second tree).
       Our buttons are only ever disabled while busy (setBusy/setSendBusy set aria-busy), so put that state back whenever
       something else changes it. Left alone: Buunto's own picker buttons, and the garden extras grid's, which keep their own
       disabled state (guarding them would re-enable "+ Add" mid-request). "Save and continue to cart" is guarded too, so
       Buunto can't swallow the tap: while Buunto saves a changed date, waitForDateAttribute holds the basket back until the
       basket's date equals the one picked in the field. */
    if ('MutationObserver' in window) {
      const keepOwnState = btn => {
        const busy = btn.getAttribute('aria-busy') === 'true';
        if (btn.disabled !== busy) btn.disabled = busy;
      };
      const sweepGuard = new MutationObserver(records => records.forEach(record => keepOwnState(record.target)));
      $$('button').filter(btn => !btn.closest('.pdp3-date, .pdp3-addons')).forEach(btn => {
        keepOwnState(btn);
        sweepGuard.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      });
    }

    /* ---------- Step 1: card message ---------- */
    const messageField = $('[data-pdp3-message]');
    const messageNext = $('[data-pdp3-message-next]');

    function renderMessage() {
      if (!messageField) return;
      const hasText = messageField.value.trim().length > 0;
      $('[data-pdp3-message-count]').textContent = messageField.value.length + '/' + messageField.maxLength;
      messageNext.textContent = hasText ? 'Continue' : 'Skip';
      messageNext.classList.toggle('pdp3-btn--outline', !hasText);
    }
    if (messageField) messageField.addEventListener('input', renderMessage);

    /**
     * Save the card message and/or the included engraving on the tree's line: `changes` is { message } and/or { engraving }.
     * Both are sent every time, since a property change replaces all of a line's properties. A cleared one is sent blank:
     * sending no properties at all would keep the old ones.
     */
    function saveTree(changes) {
      const item = cachedItem(flow.treeKey);
      if (!item) {
        flow.treeKey = null; flow.variantId = null; // so the next Send adds the tree again
        return Promise.reject(new Error('Your tree is no longer in the basket. Please go back and send it again.'));
      }
      const next = Object.assign({ message: flow.message, engraving: flow.engraving, preview: flow.preview }, changes);
      const engravingChanged = includedPlaque && next.engraving !== flow.engraving;
      if (engravingChanged) next.preview = ''; // the old picture shows the old words
      return (engravingChanged ? plaquePreviewFile(next.engraving) : Promise.resolve(null)).then(previewFile => {
        const properties = { 'Gift Message': next.message };
        if (includedPlaque) Object.assign(properties, engravingProperties(next.engraving, next.preview));
        const saved = () => { flow.message = next.message; flow.engraving = next.engraving; flow.preview = next.preview; };
        const quantity = item.quantity;
        const variantId = flow.variantId;
        if (quantity > 1 || previewFile) {
          // Ours comes off its line and goes back on a new one: a picture can only be uploaded with a new line, and a tree that
          // merged with one already in the basket mustn't put its message or engraving on the shopper's other tree.
          // Off first, as a tree with one left in stock can't be in the basket twice, even for a moment.
          const oldKey = flow.treeKey;
          const oldProperties = item.properties || {};
          return decrementLine(oldKey)
            .then(() => addTree(variantId, properties, previewFile).catch(err => {
              // Put the tree back as it was: a quantity change keeps a merged line's key; a removed line needs adding again.
              const restore = quantity > 1 ? changeLine(oldKey, quantity) : addLines([{ id: variantId, quantity: 1, properties: oldProperties }])
                .then(({ res }) => { const back = ((res && res.items) || [])[0]; flow.treeKey = back ? back.key : null; });
              return restore.catch(() => {}).then(() => { throw err; });
            }))
            .then(({ res, cart: updated }) => {
              const added = ((res && res.items) || []).find(entry => entry.variant_id === variantId);
              flow.treeKey = added ? added.key : null;
              if (added && previewFile) next.preview = (added.properties || {})[PREVIEW_PROPERTY] || '';
              saved();
              onCartChanged(updated);
            });
        }
        return saveTreeProperties(properties, quantity, variantId).then(saved);
      });
    }

    /** Change our tree line's properties in place (see saveTree). */
    function saveTreeProperties(properties, quantity, variantId) {
      const keysBefore = window.CartJS.cart.items.map(entry => entry.key);
      return changeLine(flow.treeKey, quantity, properties).then(updated => {
        // New properties give the line a new key: ours is the line with this variant and these properties,
        // and if another line matches too (the shopper's own tree, when everything is cleared), the one whose key is new.
        const matches = updated.items.filter(entry => entry.variant_id === variantId && sameProperties(entry.properties, properties));
        const mine = matches.find(entry => entry.key === flow.treeKey) || matches.find(entry => keysBefore.indexOf(entry.key) === -1) || matches[0];
        flow.treeKey = mine ? mine.key : null;
        onCartChanged(updated);
      });
    }

    /** Add one tree with these properties. With a picture, as a form post: the only way Shopify takes a file property. */
    function addTree(variantId, properties, file) {
      if (!file) return addLines([{ id: variantId, quantity: 1, properties: properties }]);
      const form = new FormData();
      form.append('items[0][id]', variantId);
      form.append('items[0][quantity]', '1');
      Object.keys(properties).forEach(key => {
        if (key !== PREVIEW_PROPERTY) form.append('items[0][properties][' + key + ']', properties[key]);
      });
      form.append('items[0][properties][' + PREVIEW_PROPERTY + ']', file, 'engraving-image.jpg');
      return fetch('/cart/add.js', { method: 'POST', body: form, headers: { Accept: 'application/json' } })
        .then(res => res.json().then(json => {
          if (!res.ok) throw { responseJSON: json }; // the shape errorMessage() reads
          return json;
        }))
        .then(res => refreshCart().then(cart => ({ res, cart })));
    }

    on('[data-pdp3-message-next]', 'click', () => {
      const text = messageField.value.trim();
      if (text === flow.message) { nextStep(); return; }
      setBusy(messageNext, true);
      saveTree({ message: text })
        .then(nextStep)
        .catch(err => showStepError(err instanceof Error ? err.message : errorMessage(err)))
        .finally(() => setBusy(messageNext, false));
    });

    /* ---------- Step 2: engraved plaque ---------- */
    const engr = $('[data-pdp3-engr]');
    const previewImg = $('[data-pdp3-preview-img]');
    const engravingInput = $('[data-pdp3-engraving]');
    const shapeButtons = $$('[data-pdp3-shape]');
    const plaqueCarousel = carousel($('[data-pdp3-car]'), $('[data-pdp3-car-dots]'), null, $$('[data-pdp3-car-arrow]'));
    const defaultPlaqueVariant = () => (config.plaque ? (config.plaque.variants.find(v => /heart/i.test(v.title)) || config.plaque.variants[0]) : null);
    let plaqueVariant = defaultPlaqueVariant();
    let measureContext = null;
    const ENGRAVING_MAX_LINES = 4;
    const ENGRAVING_FONT = '400 {px}px "EB Garamond", Garamond, Georgia, serif'; // matches the engravers' serif
    let engravingFontRequested = false;

    /** Load the engraving serif only when the plaque step opens, then refit once it has arrived. */
    function loadEngravingFont() {
      if (engravingFontRequested) return;
      engravingFontRequested = true;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400&display=swap';
      link.addEventListener('load', () => {
        if (document.fonts && document.fonts.load) document.fonts.load('400 24px "EB Garamond"').then(fitEngraving).catch(() => {});
      });
      document.head.appendChild(link);
    }

    function shapeFor(variant) { return config.shapes[handleize(variant.title)] || config.shapes.rectangle; }

    function fitEngraving() {
      if (!engr || !plaqueVariant) return;
      const width = engr.clientWidth;
      const height = engr.clientHeight;
      if (!width || !height) return;
      const shape = shapeFor(plaqueVariant);
      const scale = (previewImg.clientWidth || 335) / 335; // shape sizes were tuned on a 335px-wide preview
      measureContext = measureContext || document.createElement('canvas').getContext('2d');
      const size = fitFontSize(engr.textContent, { fmax: shape.fmax * scale, width: width },
        (word, px) => { measureContext.font = ENGRAVING_FONT.replace('{px}', px); return measureContext.measureText(word).width; },
        px => { engr.style.fontSize = px + 'px'; return engr.scrollHeight <= height + 1; });
      engr.style.fontSize = size + 'px';
    }

    /**
     * The included plaque's photo with the engraving drawn on it as the preview shows it, as a JPEG for the order.
     * Resolves null when there is nothing to draw, or the picture can't be made: the words still go on the line.
     */
    function plaquePreviewFile(words) {
      const shape = config.shapes.included;
      if (!words || !shape || !shape.image) return Promise.resolve(null);
      const text = twoWordLines(words); // laid out as the preview shows it
      const photo = new Image();
      photo.crossOrigin = 'anonymous'; // or the canvas can't be saved
      const loaded = new Promise((resolve, reject) => { photo.onload = resolve; photo.onerror = reject; });
      photo.src = shape.image;
      loadEngravingFont();
      const font = document.fonts && document.fonts.load ? document.fonts.load('400 24px "EB Garamond"').catch(() => {}) : null;
      return Promise.all([loaded, font]).then(() => {
        const canvas = document.createElement('canvas');
        canvas.width = photo.naturalWidth;
        canvas.height = photo.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(photo, 0, 0);
        // Same sizing as fitEngraving, at the photo's own size
        const scale = canvas.width / 335;
        const width = canvas.width * shape.w / 100;
        const height = canvas.height * shape.h / 100;
        const measureAt = px => piece => { ctx.font = ENGRAVING_FONT.replace('{px}', px); return ctx.measureText(piece).width; };
        const size = fitFontSize(text, { fmax: shape.fmax * scale, width: width },
          (piece, px) => measureAt(px)(piece),
          px => balancedLines(text, width, measureAt(px)).length * px * 1.1 <= height + 1);
        const lines = balancedLines(text, width, measureAt(size));
        const lineHeight = size * 1.1; // .pdp3-engr line-height
        const x = canvas.width * shape.x / 100;
        const top = canvas.height * shape.y / 100 - lines.length * lineHeight / 2;
        ctx.font = ENGRAVING_FONT.replace('{px}', size);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        lines.forEach((line, i) => {
          const y = top + lineHeight * (i + 0.5);
          ctx.fillStyle = 'rgba(255,246,205,.45)'; // .pdp3-engr's light text-shadow, 1px down at the preview's size
          ctx.fillText(line, x, y + scale);
          ctx.fillStyle = shape.color || 'rgba(48,36,10,.84)'; // as renderShape colours the preview
          ctx.fillText(line, x, y);
        });
        return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.88));
      }).catch(() => null);
    }

    function renderEngraving() {
      if (!engravingInput) return;
      const text = engravingInput.value;
      $('[data-pdp3-engraving-count]').textContent = text.length + '/' + plaqueVariant.max;
      renderEngravingNext();
      if (!engr) return; // an included plaque with no photo on its metafield entry: no preview
      engr.textContent = '';
      if (text.trim()) {
        engr.textContent = includedPlaque ? twoWordLines(text) : text; // the included plaque is narrow: two words, two lines
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'pdp3-engr__ph';
        placeholder.textContent = 'Your words';
        engr.append(placeholder);
      }
      fitEngraving();
    }

    function renderShape() {
      if (!plaqueVariant || !previewImg) { renderEngraving(); return; }
      const shape = shapeFor(plaqueVariant);
      if (!includedPlaque) { // an included plaque's photo and its alt text come from the page
        previewImg.src = shape.image;
        previewImg.alt = plaqueVariant.title + ' brass plaque hanging in a tree';
      }
      engr.style.left = shape.x + '%';
      engr.style.top = shape.y + '%';
      engr.style.width = shape.w + '%';
      engr.style.height = shape.h + '%';
      engr.style.color = shape.color || ''; // a shape's own engraving colour, else the stylesheet's
      shapeButtons.forEach(btn => btn.setAttribute('aria-pressed', String(Number(btn.dataset.pdp3Shape) === plaqueVariant.id)));
      engravingInput.maxLength = plaqueVariant.max;
      engravingInput.placeholder = 'Your engraving message, up to ' + plaqueVariant.max + ' characters';
      if (engravingInput.value.length > plaqueVariant.max) engravingInput.value = engravingInput.value.slice(0, plaqueVariant.max);
      if (!includedPlaque) $('[data-pdp3-plaque-price]').textContent = money(plaqueVariant.price, true);
      renderEngraving();
    }

    function renderPlaque() {
      if (!config.plaque) return;
      if (includedPlaque) {
        if (previewImg) previewImg.loading = 'eager';
        loadEngravingFont();
        requestAnimationFrame(renderShape);
        return;
      }
      const added = !!flow.plaqueKey;
      $('[data-pdp3-plaque-edit]').hidden = added;
      $('[data-pdp3-plaque-added]').hidden = !added;
      $('[data-pdp3-add-plaque]').hidden = added;
      $('[data-pdp3-skip-plaque]').hidden = added;
      $('[data-pdp3-plaque-continue]').hidden = !added;
      if (added) {
        const price = document.createElement('span');
        price.className = 'pdp3-price-text';
        price.textContent = money(flow.plaque.price, true);
        const addedText = $('[data-pdp3-plaque-added-text]');
        addedText.textContent = '';
        addedText.append(flow.plaque.title + ' plaque added · ', price);
      }
      plaqueCarousel.show(0, false);
      // The photos are lazy, and a swipe can outrun a lazy image in a sideways scroller; fetch them all once the step opens
      plaqueCarousel.slides.forEach(slide => slide.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; }));
      // Same for the other shapes' preview photos, so picking one doesn't show the old shape while it downloads
      Object.values(config.shapes).forEach(shape => { if (shape.image) new Image().src = shape.image; });
      loadEngravingFont();
      requestAnimationFrame(renderShape); // the step is laid out by the next frame, so the fit can measure
    }

    shapeButtons.forEach(btn => btn.addEventListener('click', () => {
      plaqueVariant = config.plaque.variants.find(v => v.id === Number(btn.dataset.pdp3Shape)) || plaqueVariant;
      renderShape();
      plaqueCarousel.show(0); // back to the preview slide, which now shows the chosen shape
    }));
    if (engravingInput) {
      engravingInput.addEventListener('input', () => {
        const engravingError = $('[data-pdp3-engraving-error]'); // not on an included plaque, where the engraving is optional
        if (engravingError) engravingError.hidden = true;
        engravingInput.classList.remove('is-invalid');
        const limited = limitLines(engravingInput.value, ENGRAVING_MAX_LINES);
        if (limited !== engravingInput.value) {
          const caret = Math.min(engravingInput.selectionStart, limited.length);
          engravingInput.value = limited;
          engravingInput.setSelectionRange(caret, caret);
        }
        renderEngraving();
      });
    }
    if (previewImg) previewImg.addEventListener('load', fitEngraving);
    window.addEventListener('resize', () => { if (flow.step === 'plaque') fitEngraving(); });

    on('[data-pdp3-add-plaque]', 'click', event => {
      const btn = event.currentTarget;
      const text = engravingInput.value.trim();
      if (!text) {
        $('[data-pdp3-engraving-error]').hidden = false;
        engravingInput.classList.add('is-invalid');
        engravingInput.focus();
        return;
      }
      const variant = plaqueVariant;
      setBusy(btn, true);
      cartRequest(opts => window.CartJS.addItem(variant.id, 1, { 'Engraving': text, '_Engraving Type': variant.title, '_For Product': config.plaque.forProduct }, opts))
        .then(item => refreshCart().then(cart => {
          flow.plaqueKey = item.key;
          flow.plaque = { title: variant.title, price: variant.price };
          onCartChanged(cart);
          nextStep();
        }))
        .catch(err => showStepError(errorMessage(err)))
        .finally(() => setBusy(btn, false));
    });
    on('[data-pdp3-skip-plaque]', 'click', nextStep);
    on('[data-pdp3-plaque-continue]', 'click', nextStep);
    on('[data-pdp3-remove-plaque]', 'click', event => {
      const btn = event.currentTarget;
      if (btn.disabled || !flow.plaqueKey) return; // a second tap would take one off whichever line is now in its place
      setBusy(btn, true);
      decrementLine(flow.plaqueKey)
        .then(cart => { flow.plaqueKey = null; flow.plaque = null; onCartChanged(cart); renderPlaque(); })
        .catch(err => showStepError(errorMessage(err)))
        .finally(() => setBusy(btn, false));
    });

    // Included plaque: no plaque to add, so the engraving is optional and goes on the tree's line (like the card message).
    const engravingNext = $('[data-pdp3-engraving-next]');
    function renderEngravingNext() {
      if (!engravingNext) return;
      const hasText = engravingInput.value.trim().length > 0;
      engravingNext.textContent = hasText ? 'Continue' : 'Skip';
      engravingNext.classList.toggle('pdp3-btn--outline', !hasText);
    }
    on('[data-pdp3-engraving-next]', 'click', () => {
      const text = engravingInput.value.trim();
      if (text === flow.engraving) { nextStep(); return; }
      setBusy(engravingNext, true);
      saveTree({ engraving: text })
        .then(nextStep)
        .catch(err => showStepError(err instanceof Error ? err.message : errorMessage(err)))
        .finally(() => setBusy(engravingNext, false));
    });

    /* ---------- Step 3: garden extras (hampers only), today's add-ons grid ---------- */
    // The grid adds and removes its own lines (straight to /cart/add.js, not through CartJS) and fires addon-grid:change after each.
    const addonsNext = $('[data-pdp3-addons-next]');
    const addedExtras = () => $$('.pdp3-addons .seller-add-to-cart-wrap').filter(wrap => wrap.dataset.lineKey);

    function renderAddons() {
      if (!addonsNext) return;
      const any = addedExtras().length > 0;
      addonsNext.textContent = any ? 'Continue' : 'Skip';
      addonsNext.classList.toggle('pdp3-btn--outline', !any);
    }
    /** Show a card as not added (its line, if still in the basket, stays there). */
    function clearExtra(wrap) {
      wrap.dataset.lineKey = '';
      const add = wrap.querySelector('.add-trigger');
      const added = wrap.querySelector('.added-state');
      if (add) add.style.display = '';
      if (added) added.style.display = 'none';
    }
    root.addEventListener('addon-grid:change', () => {
      renderAddons();
      refreshCart().then(onCartChanged).catch(() => {}); // the order total, and CartJS's copy of the basket
    });
    on('[data-pdp3-addons-next]', 'click', nextStep);

    /* ---------- Last step, delivery options split test (TSK-11502): <delivery-options> (assets/delivery-options.js) ---------- */
    // It shows Special, Premium or Free to Eraya's test variation and saves the choice; the steps add its price to the total.
    const deliveryEl = $('delivery-options');
    /** Whether this visitor gets the delivery options instead of Buunto's date (worked out when the delivery step opens). */
    const deliveryTestOn = () => !!(deliveryEl && deliveryEl.active);
    if (deliveryEl) deliveryEl.addEventListener('delivery-options:change', renderOrderTotal);

    /* ---------- Last step: delivery date (Buunto) ---------- */
    // Buunto creates an empty #buunto-date-picker mount on its own; the field itself only appears after refreshWidget().
    function buuntoWidget() { return root.querySelector('.pdp3-date #buunto-date-picker-input'); }

    function onDateStep() {
      const testOn = !!(deliveryEl && typeof deliveryEl.refresh === 'function' && deliveryEl.refresh());
      const current = $('[data-pdp3-date-current]');
      const title = $('[data-pdp3-date-title]');
      if (current) current.hidden = testOn;
      if (title) {
        if (!title.dataset.currentTitle) title.dataset.currentTitle = title.textContent;
        title.textContent = testOn ? title.dataset.testTitle : title.dataset.currentTitle;
      }
      if (testOn) return;
      const picker = window.Buunto && window.Buunto.datePicker;
      if (picker && typeof picker.refreshWidget === 'function' && !buuntoWidget()) {
        try { picker.refreshWidget(); } catch (e) { /* Buunto re-renders on its own cart listener too */ }
      }
      preselectEarliestDate();
    }

    /**
     * Pick the earliest delivery date for the shopper, once per gift, unless a date is already picked (or kept in the
     * basket from an earlier visit). Buunto's own "preselect" setting would change today's page too, so it stays off.
     * selectDate takes "YYYY-MM-DD"; Buunto clears a date it doesn't offer, so a wrong guess leaves the field empty.
     */
    function preselectEarliestDate() {
      const started = Date.now();
      (function poll() {
        if (!flow.open || flow.step !== 'date' || flow.datePreselected) return;
        const picker = window.Buunto && window.Buunto.datePicker;
        if (!picker || typeof picker.selectDate !== 'function' || !buuntoWidget()) {
          if (Date.now() - started < 8000) setTimeout(poll, 200);
          return;
        }
        flow.datePreselected = true;
        setTimeout(() => { // let Buunto restore a date kept in the basket first
          if (!flow.open || flow.step !== 'date') { flow.datePreselected = false; return; } // try again next time
          if (pickedDate()) return;
          const cutOff = readCutOff(picker);
          if (cutOff && cutOff.day) picker.selectDate(cutOff.day);
        }, 300);
      })();
    }

    // Clear "Please choose a delivery date" as soon as a date is picked (Buunto fires no event, so watch its field).
    const dateBox = $('.pdp3-date');
    const dateErrorEl = $('[data-pdp3-date-error]');
    if (dateBox && dateErrorEl && 'MutationObserver' in window) {
      new MutationObserver(() => {
        const picker = window.Buunto && window.Buunto.datePicker;
        if (!dateErrorEl.hidden && picker && typeof picker.hasDateError === 'function' && !picker.hasDateError()) dateErrorEl.hidden = true;
      }).observe(dateBox, { childList: true, subtree: true, characterData: true });
    }

    /** The date picked in Buunto's field (its hidden input reads like "Thu 08 Oct 2026", as the cart attribute does). */
    function pickedDate() {
      const input = root.querySelector('.pdp3-date input[name="attributes[' + DATE_ATTRIBUTE + ']"]');
      return input ? input.value.trim() : '';
    }

    /**
     * Buunto saves the date with its own request; wait until the basket holds the date now picked, not just any date
     * (an older one is still there while Buunto saves a changed date, or left from an earlier visit).
     */
    function waitForDateAttribute(timeoutMs) {
      const started = Date.now();
      return new Promise(resolve => {
        (function poll() {
          fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
            .then(res => res.json())
            .then(cart => {
              const saved = String((cart.attributes && cart.attributes[DATE_ATTRIBUTE]) || '').trim();
              if (saved && saved === pickedDate()) { resolve(true); return; } // read the field on every poll
              if (Date.now() - started > timeoutMs) { resolve(false); return; }
              setTimeout(poll, 300);
            })
            .catch(() => resolve(false));
        })();
      });
    }

    on('[data-pdp3-to-cart]', 'click', event => {
      const btn = event.currentTarget;
      if (deliveryTestOn()) {
        if (!deliveryEl.validate()) return;
        setBusy(btn, true);
        hideStepError();
        deliveryEl.saving.then(() => {
          if (!flow.open) { setBusy(btn, false); return; }
          markLeaving();
          trackFlowEnd('cart');
          window.location.href = '/cart';
        }).catch(err => {
          setBusy(btn, false);
          showStepError(errorMessage(err));
        });
        return;
      }
      const picker = window.Buunto && window.Buunto.datePicker;
      const dateError = $('[data-pdp3-date-error]');
      const canCheck = picker && typeof picker.hasDateError === 'function' && buuntoWidget();
      if (canCheck && picker.hasDateError()) { dateError.hidden = false; return; }
      dateError.hidden = true;
      setBusy(btn, true);
      // Give Buunto up to 3s to save the date, so the basket shows it; go on to the basket either way.
      (canCheck ? waitForDateAttribute(3000) : Promise.resolve(false)).then(() => {
        if (!flow.open) { setBusy(btn, false); return; } // the shopper closed the steps while we waited
        markLeaving();
        trackFlowEnd('cart');
        window.location.href = '/cart';
      });
    });

    on('[data-pdp3-save-shop]', 'click', () => {
      const parts = [];
      if (flow.message) parts.push('card message');
      if (flow.plaqueKey) parts.push('plaque');
      if (flow.engraving) parts.push('engraving');
      if (addedExtras().length) parts.push('garden extras');
      if (deliveryTestOn() ? deliveryEl.choice : pickedDate()) parts.push(deliveryTestOn() ? 'delivery choice' : 'delivery date');
      showToast(parts);
      resetFlow();
      leaveSteps('continue_shopping');
    });

    function showToast(parts) {
      const toast = $('[data-pdp3-toast]');
      const text = $('[data-pdp3-toast-text]');
      const variant = config.variants.find(v => v.id === flow.variantId);
      const strong = document.createElement('b');
      strong.textContent = config.productTitle + (variant && variant.title !== 'Default Title' ? ', ' + variant.title : '');
      text.textContent = '';
      text.append(strong, ' is in your basket' + (parts.length ? ' with your ' + joinList(parts) : '') + '.');
      toast.hidden = false;
      lastFocus = toast; // focus (and scroll to) the message once the steps close
    }

    /** After "Save and continue shopping" the next send is a new gift. */
    function resetFlow() {
      Object.assign(flow, { treeKey: null, hamperKey: null, plaqueKey: null, variantId: null, hamper: false, message: '', engraving: '', preview: '', plaque: null, datePreselected: false });
      if (messageField) messageField.value = '';
      if (engravingInput) engravingInput.value = '';
      plaqueVariant = defaultPlaqueVariant(); // Heart again
      addedExtras().forEach(clearExtra); // the next hamper starts with "+ Add" on every card
    }

    /* ---------- Back from checkout or the basket: the page comes back from the back-forward cache as it was left ---------- */
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      $$('[aria-busy="true"]').forEach(btn => setBusy(btn, false)); // send buttons and step buttons
      refreshCart().then(cart => {
        onCartChanged(cart);
        // The basket may have been edited meanwhile: forget lines that have gone, so the next Send adds them again.
        if (flow.treeKey && !lineForKey(cart, flow.treeKey)) { flow.treeKey = null; flow.variantId = null; }
        if (flow.hamperKey && !lineForKey(cart, flow.hamperKey)) { flow.hamperKey = null; flow.hamper = false; }
        if (flow.plaqueKey && !lineForKey(cart, flow.plaqueKey)) { flow.plaqueKey = null; flow.plaque = null; }
        addedExtras().forEach(wrap => { if (!lineForKey(cart, wrap.dataset.lineKey)) clearExtra(wrap); });
        if (flow.open && flow.step === 'plaque') renderPlaque();
        if (flow.open && flow.step === 'addons') renderAddons();
      }).catch(() => {});
    });

    /* ---------- GA4 funnel events: same names as assets/atb-popup-tracking.js, plus pdp_version ---------- */
    const STEP_EVENTS = { message: 'gift_message', plaque: 'personalisation', addons: 'hamper_addons', date: 'delivery_date' };
    const COMPLETE_REASONS = ['checkout', 'cart', 'continue_shopping'];
    let flowEnded = true;
    let leaving = false;

    function track(eventName, params) {
      const payload = Object.assign({ send_to: 'G-YJ7WK5BCYZ', product_handle: config.productHandle, pdp_version: 'v3' }, params);
      if (typeof window.gtag === 'function') { window.gtag('event', eventName, payload); return; }
      window.dataLayer = window.dataLayer || [];
      (function () { window.dataLayer.push(arguments); })('event', eventName, payload); // same shape gtag() queues
    }
    function trackStep(name, index) {
      track('atb_step_' + STEP_EVENTS[name], { step_name: STEP_EVENTS[name], step_number: index + 1, total_steps: steps.length });
    }
    function trackFlowStart() { flowEnded = false; }
    function trackFlowEnd(reason) {
      if (flowEnded) return;
      flowEnded = true;
      const where = { step_name: STEP_EVENTS[flow.step], step_number: steps.indexOf(flow.step) + 1, total_steps: steps.length };
      if (COMPLETE_REASONS.indexOf(reason) !== -1) track('atb_popup_complete', Object.assign({ destination: reason }, where));
      else track('atb_popup_exit', Object.assign({ exit_reason: reason }, where));
    }
    function markLeaving() { leaving = true; }
    window.addEventListener('pagehide', () => { if (flow.open && !leaving) trackFlowEnd('left_page'); });
    // Back from checkout can restore the page with the steps still open (the checkout click ended tracking): start counting again.
    window.addEventListener('pageshow', event => {
      if (!event.persisted || !flow.open) return;
      leaving = false;
      trackFlowStart();
    });
  }

  document.querySelectorAll('[data-pdp3]').forEach(initPDP3);
})();
